import { useState } from "react";
import axios from "axios";
import { Send, CheckCircle2, Loader2, Mail, Phone, MapPin } from "lucide-react";
import { Toaster, toast } from "sonner";
import { BRAND } from "../data/brand";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  (typeof window !== "undefined" ? window.location.origin : "");
const API = `${BACKEND_URL}/api`;

const VEHICLE_TYPES = ["Auto", "SUV", "Moto", "Furgone", "Camper"];
const PACKAGES = ["Esterno Nanotech", "Interno Profondo", "Pacchetto Completo", "Detailing Premium"];

const FieldLabel = ({ children, htmlFor }) => (
  <label
    htmlFor={htmlFor}
    className="overline block mb-2 text-[10px]"
  >
    {children}
  </label>
);

const inputCls =
  "w-full bg-white/5 border border-white/10 text-white placeholder:text-nexa-muted/60 px-4 py-3 font-sans text-sm focus:outline-none focus:border-nexa-blue focus:ring-1 focus:ring-nexa-blue/50 transition-colors";

export default function QuoteForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    vehicle_type: "SUV",
    package: "Pacchetto Completo",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Compila i campi obbligatori (nome, email, telefono).");
      return;
    }
    setLoading(true);

    // Invio al backend: manda l'email via Brevo e salva la richiesta su MongoDB.
    let ok = false;
    try {
      await axios.post(`${API}/quotes`, form);
      ok = true;
    } catch (err) {
      console.error("Errore invio richiesta:", err);
    }

    setLoading(false);

    if (ok) {
      setDone(true);
      toast.success("Richiesta inviata! Ti contatteremo al più presto.");
      setForm({ name: "", email: "", phone: "", vehicle_type: "SUV", package: "Pacchetto Completo", message: "" });
    } else {
      toast.error("Errore durante l'invio. Riprova o contattaci su WhatsApp.");
    }
  };

  return (
    <section id="preventivo" data-testid="quote-section" className="relative py-24 md:py-32 overflow-hidden">
      <Toaster position="top-center" theme="dark" richColors />
      <div className="absolute inset-0 bg-radial-spot opacity-50" />
      <div className="absolute top-1/3 -left-40 w-80 h-80 bg-nexa-blue/10 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-px bg-nexa-blue" />
            <span className="overline">Richiedi Preventivo</span>
          </div>
          <div className="heading text-3xl md:text-5xl text-white mb-6 leading-tight">
            Un lavaggio su misura,<br />
            <span className="text-nexa-blue">senza sorprese.</span>
          </div>
          <p className="text-nexa-muted text-base md:text-lg leading-relaxed mb-10">
            Compila il modulo qui a fianco. Ti risponderemo rapidamente con un preventivo personalizzato per il tuo veicolo.
          </p>

          <div className="space-y-5">
            <a
              href={`mailto:${BRAND.email}`}
              data-testid="contact-email"
              className="flex items-center gap-4 group"
            >
              <div className="w-11 h-11 flex items-center justify-center border border-white/10 group-hover:border-nexa-blue group-hover:text-nexa-blue transition-colors">
                <Mail size={18} />
              </div>
              <div>
                <div className="text-[10px] text-nexa-muted uppercase tracking-widest">Email</div>
                <div className="text-white text-sm">{BRAND.email}</div>
              </div>
            </a>
            <a
              href={`tel:${BRAND.phone.replace(/\s/g, "")}`}
              data-testid="contact-phone"
              className="flex items-center gap-4 group"
            >
              <div className="w-11 h-11 flex items-center justify-center border border-white/10 group-hover:border-nexa-blue group-hover:text-nexa-blue transition-colors">
                <Phone size={18} />
              </div>
              <div>
                <div className="text-[10px] text-nexa-muted uppercase tracking-widest">Telefono</div>
                <div className="text-white text-sm">{BRAND.phoneDisplay}</div>
              </div>
            </a>
            <a
              href={BRAND.mapUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-address"
              className="flex items-center gap-4 group"
            >
              <div className="w-11 h-11 flex items-center justify-center border border-white/10 group-hover:border-nexa-blue group-hover:text-nexa-blue transition-colors">
                <MapPin size={18} />
              </div>
              <div>
                <div className="text-[10px] text-nexa-muted uppercase tracking-widest">Sede</div>
                <div className="text-white text-sm">{BRAND.addressLong}</div>
              </div>
            </a>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="card-surface p-8 md:p-10 relative">
            {done ? (
              <div className="py-16 text-center" data-testid="quote-success">
                <CheckCircle2 size={56} className="text-nexa-blue mx-auto mb-6" />
                <div className="heading text-2xl text-white mb-3 uppercase tracking-wider">Richiesta inviata</div>
                <p className="text-nexa-muted mb-8">
                  Grazie! Ti contatteremo al più presto con il tuo preventivo.
                </p>
                <button
                  className="btn-outline"
                  onClick={() => setDone(false)}
                  data-testid="quote-new-request"
                >
                  Nuova Richiesta
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="quote-form" className="space-y-5">
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <FieldLabel htmlFor="name">Nome e Cognome *</FieldLabel>
                    <input
                      id="name"
                      type="text"
                      required
                      value={form.name}
                      onChange={update("name")}
                      className={inputCls}
                      placeholder="Mario Rossi"
                      data-testid="quote-input-name"
                    />
                  </div>
                  <div>
                    <FieldLabel htmlFor="email">Email *</FieldLabel>
                    <input
                      id="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={update("email")}
                      className={inputCls}
                      placeholder="mario@esempio.it"
                      data-testid="quote-input-email"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <FieldLabel htmlFor="phone">Telefono *</FieldLabel>
                    <input
                      id="phone"
                      type="tel"
                      required
                      value={form.phone}
                      onChange={update("phone")}
                      className={inputCls}
                      placeholder="+39 333 1234567"
                      data-testid="quote-input-phone"
                    />
                  </div>
                  <div>
                    <FieldLabel htmlFor="vehicle_type">Tipo di Veicolo</FieldLabel>
                    <select
                      id="vehicle_type"
                      value={form.vehicle_type}
                      onChange={update("vehicle_type")}
                      className={inputCls}
                      data-testid="quote-input-vehicle"
                    >
                      {VEHICLE_TYPES.map((v) => (
                        <option key={v} value={v} className="bg-nexa-surface">{v}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <FieldLabel htmlFor="package">Pacchetto</FieldLabel>
                  <select
                    id="package"
                    value={form.package}
                    onChange={update("package")}
                    className={inputCls}
                    data-testid="quote-input-package"
                  >
                    {PACKAGES.map((p) => (
                      <option key={p} value={p} className="bg-nexa-surface">{p}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <FieldLabel htmlFor="message">Messaggio</FieldLabel>
                  <textarea
                    id="message"
                    rows={4}
                    value={form.message}
                    onChange={update("message")}
                    className={inputCls + " resize-none"}
                    placeholder="Dettagli aggiuntivi, modello veicolo, preferenze..."
                    data-testid="quote-input-message"
                  />
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4">
                  <p className="text-xs text-nexa-muted max-w-xs">
                    Inviando accetti di essere contattato. I tuoi dati non saranno condivisi.
                  </p>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="quote-submit"
                    className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Invio...
                      </>
                    ) : (
                      <>
                        Invia Richiesta
                        <Send size={16} />
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
